import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import {
  space,
  Avatar,
  H3,
  Text,
  color,
  radius,
  BaseButton,
  device,
} from '@ticketswap/solar'
import TraderInformationDialog from '../TraderInformationDialog'
import { CheckmarkRounded } from '@ticketswap/comets'

const Container = styled.header`
  position: relative;
  background-color: ${color.stardust};
  border-radius: ${radius.lg};
  display: flex;

  em {
    color: ${color.spaceLight};
    font-style: normal;
    display: block;
  }
`

const AvatarContainer = styled.div`
  margin-bottom: ${space[8]};

  @media ${device.tablet} {
    margin-bottom: 0;
    margin-right: ${space[16]};
  }
`

const NameAndAvatar = styled.div`
  text-align: center;

  @media ${device.tablet} {
    text-align: left;
    display: flex;
    align-items: center;
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:first-of-type {
    min-width: 35%;
    padding-right: ${space[16]};

    @media ${device.tablet} {
      min-width: 50%;
      align-items: center;
    }
  }

  &:last-of-type {
    min-width: 65%;
    padding-left: ${space[16]};
    border-left: 1px solid ${color.spaceLightestAlpha};

    @media ${device.tablet} {
      min-width: 50%;
      align-items: flex-start;
    }
  }
`

const VerificationsItem = styled.li`
  position: relative;
  padding-left: ${space[32]};

  & + ${() => VerificationsItem} {
    margin-top: ${space[4]};
  }

  .icon {
    position: absolute;
    left: 0;
    top: 0;
    color: ${color.titan};
  }
`

function SellerInformation({ t, seller = {}, showLastname, ...props }) {
  return (
    <Container {...props}>
      <Column>
        <NameAndAvatar>
          <AvatarContainer>
            <Avatar src={seller.avatarBig} size={64} />
          </AvatarContainer>
          <div>
            <H3>
              {showLastname && seller.lastname
                ? `${seller.firstname} ${seller.lastname}`
                : seller.firstname}
            </H3>
            <Text>
              <em>{seller.city}</em>
            </Text>
          </div>
        </NameAndAvatar>
      </Column>
      <Column>
        <ul>
          {seller.successfullySoldCount > 0 && (
            <VerificationsItem>
              <CheckmarkRounded size={24} />
              {t('listing:purchaser.ticketsSold', {
                count: seller.successfullySoldCount,
              })}
              {seller.successfullySoldCount > 100 && (
                <TraderInformationDialog t={t} {...props}>
                  {({ getToggleProps }) => (
                    <em>
                      <BaseButton {...getToggleProps()}>
                        {t('listing:purchaser.whySoMany')}
                      </BaseButton>
                    </em>
                  )}
                </TraderInformationDialog>
              )}
            </VerificationsItem>
          )}
          <VerificationsItem>
            <CheckmarkRounded size={24} />
            {t('listing:purchaser.phoneVerified')}
          </VerificationsItem>
          {seller.facebookAccount && (
            <VerificationsItem>
              <CheckmarkRounded size={24} />
              {t('listing:purchaser.facebookConnected')}{' '}
              {seller.facebookAccount?.friendsCount > 0 && (
                <em>
                  {t('listing:purchaser.friend', {
                    count: seller.facebookAccount.friendsCount,
                  })}
                </em>
              )}
            </VerificationsItem>
          )}
        </ul>
      </Column>
    </Container>
  )
}

SellerInformation.defaultProps = {
  showLastname: true,
}

SellerInformation.propTypes = {
  t: PropTypes.func.isRequired,
  showLastname: PropTypes.bool.isRequired,
  seller: PropTypes.shape({
    avatar: PropTypes.string.isRequired,
    avatarBig: PropTypes.string.isRequired,
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string,
    city: PropTypes.string.isRequired,
    successfullySoldCount: PropTypes.number.isRequired,
    facebookAccount: PropTypes.shape({
      friendsCount: PropTypes.number.isRequired,
    }),
  }),
}

export default SellerInformation
